'use client';

import { AnimatedLink } from '@/components/AnimatedLink';
import { Card } from '@/components/Card';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import { EXPERIENCE_FILTER } from '@/components/HomeSections/media-filters';
import { getTranslation } from '@/lib/utils';
import parser, {
  DOMNode,
  HTMLReactParserOptions,
  domToReact,
} from 'html-react-parser';
import React, { HTMLAttributes, useMemo } from 'react';
import Slider, { Settings } from 'react-slick';
import './styles.css';

interface IHomeExperiencesProps extends HTMLAttributes<HTMLDivElement> {
  catalogItems: Catalog[];
  experienceUrl: string;
}

const parseOptions: HTMLReactParserOptions = {
  replace: (node, index) => {
    if (node.type === 'tag' && node.name === 'a' && node.children) {
      const href = node.attribs?.href || '/';
      return (
        <span key={index}>
          <strong>
            {domToReact(node.children as DOMNode[], parseOptions)}
          </strong>
        </span>
      );
    }
  },
};

const settings: Settings = {
  dots: true,
  infinite: true,
  speed: 700,
  slidesToShow: 2,
  slidesToScroll: 1,
  arrows: true,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
      },
    },
  ],
};

export const HomeExperiences: React.FC<IHomeExperiencesProps> = ({
  catalogItems,
  experienceUrl,
}) => {
  const lang = useLocale();
  const { home: dict } = useTranslation();

  const renderExperiences = useMemo(
    () =>
      catalogItems.map((card, index) => {
        const translation = getTranslation(card.translate, lang);

        const filteredImage =
          card.image!.mediaFilters?.find(({ name }) => name === EXPERIENCE_FILTER) ??
          card.image!.mediaFilters[0];

        return (
          <div
            key={index}
            className="inline-block w-full aspect-square desktop:aspect-[5/3] box-border px-4 mobileL:px-2 tabletLg:px-2 desktop:px-3">
            <Card
              cardImage={filteredImage.url}
              sizes="(min-width: 640px) 50vw, (min-width: 800px) 33vw, (min-width: 1200px) 25vw, 100vw"
              customStyle="aspect-auto flex h-full before:h-[40%] before:bg-home-gradient"
              imgClassName="brightness-[.8]"
              hasSubtitle
              href={`/${experienceUrl}/${translation.slug}`}
              hoverContent={
                <div className="flex justify-start">
                  <AnimatedLink
                    className="w-fit px-[10px] desktop:px-[40px] desktop:w-fit desktop:text-white mt-2"
                    asChild>
                    <div>{dict.experiences.cta.cardSubtitle}</div>
                  </AnimatedLink>
                </div>
              }>
              <div className="flex flex-col gap-3">
                <strong className="block w-full whitespace-normal text-left font-title text-4.5xl">
                  {translation.title}
                </strong>

                <div className="w-full whitespace-normal text-base font-averta-light font-medium text-white text-left leading-6">
                  {parser(translation.shortDescription || '', parseOptions)}
                </div>
              </div>
            </Card>
          </div>
        );
      }),
    [catalogItems, dict, experienceUrl, lang],
  );

  return (
    <section className="flex flex-col gap-4 items-center max-w-default pt-32 mx-auto">
      <h2
        className="text-title font-title font-bold w-full text-center"
        dangerouslySetInnerHTML={{ __html: dict.experiences.title }}
      />
      <p className="mt-4 text-gray-900 leading-[1.5] text-body text-center max-w-[90%] mx-auto">
        {parser(dict.experiences.subtitle, parseOptions)}
      </p>

      <div className="flex justify-start mb-20 mt-[30px]">
        <AnimatedLink
          className="w-fit mt-0 px-[10px] desktop:px-[40px] desktop:w-fit"
          target="_blank"
          href={dict.experiences.cta.href}>
          <div>{dict.experiences.cta.title}</div>
        </AnimatedLink>
      </div>

      <div className="relative flex flex-col justify-center w-full">
        <Slider
          {...settings}
          className="home-exp-carousel visible w-full [&>.slick-list]:overflow-visible">
          {renderExperiences}
        </Slider>
      </div>
    </section>
  );
};
